import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MessageSquare, Send, Loader2, FileText, Database, Shield, RefreshCw, Sparkles, X, Copy, Download, Bookmark } from 'lucide-react';
import {
  chatWithCase,
  getChatHistory,
  evaluateVsPublic,
  saveChatResponse,
  type ChatMessage,
  type ChatScope,
  type ChatModelPath,
  type ChatCitation,
  type EvalDiscrepancy,
} from '../api/chat';
import { triggerPublicLookup, getPublicDataRun } from '../api/review';
import { useToastStore } from '../stores/toast';
import { useAuthStore } from '../stores/auth';
import { CreateRulePanel } from './learned-rules/CreateRulePanel';
import MarkdownRenderer from './MarkdownRenderer';
import type { Case } from '../api/cases';

interface ChatPanelProps {
  caseId: string;
  /** issuer name + ticker used to trigger a fresh public-data
   * lookup from the "Evaluate vs public data" button. At least one is
   * required for the evaluate flow to work. */
  issuerName?: string | null;
  ticker?: string | null;
  /** case number used to build the export .md filename. */
  caseNumber?: string | null;
  /** notify the parent when a saved response is created/list
   * changes so the Saved responses view can refresh. */
  onSavedResponsesChange?: () => void;
}

const SCOPE_OPTIONS: { value: ChatScope; label: string; note: string }[] = [
  {
    value: 'all',
    label: 'All',
    note: 'Includes private documents -> uses the local AI',
  },
  {
    value: 'documents',
    label: 'Documents',
    note: 'Private documents -> uses the local AI',
  },
  {
    value: 'public',
    label: 'Public',
    note: 'Public information only -> can use the cloud AI',
  },
];

const MODEL_PATH_OPTIONS: { value: ChatModelPath; label: string; note: string }[] = [
  {
    value: 'auto',
    label: 'Auto',
    note: 'Model chosen by scope (docs -> local, public -> cloud)',
  },
  {
    value: 'private',
    label: 'Local',
    note: 'Force the local AI',
  },
  {
    value: 'public',
    label: 'Cloud',
    note: 'Force the cloud AI. Not available if the scope includes private documents unless PII redaction is on.',
  },
];

function citationIcon(type: ChatCitation['type']) {
  if (type === 'document') return <FileText className="h-3 w-3" />;
  if (type === 'public') return <Database className="h-3 w-3" />;
  return <MessageSquare className="h-3 w-3" />;
}

function discrepanciesBlock(discrepancies: EvalDiscrepancy[], summary: string): string {
  const header = `Discrepancies found: ${discrepancies.length}`;
  const lines = discrepancies.map(
    (d) =>
      `- Claim: ${d.claim || '(unspecified)'}\n` +
      `  Public value: ${d.public_value || '(unspecified)'}\n` +
      `  Source: ${d.source_citation || '(unspecified)'}\n` +
      `  Severity: ${d.severity_note || '(unspecified)'}`
  );
  return [header, summary, ...lines].filter(Boolean).join('\n\n');
}

export default function ChatPanel({ caseId, issuerName, ticker, caseNumber, onSavedResponsesChange }: ChatPanelProps) {
  const { addToast } = useToastStore();
  const { user } = useAuthStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [scope, setScope] = useState<ChatScope>('all');
  const [modelPath, setModelPath] = useState<ChatModelPath>('auto');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // textarea ref for auto-resize of the multiline chat input.
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // per-message action state (saving a response, copied marker).
  const [savingIdx, setSavingIdx] = useState<number | null>(null);

  // readline-style up/down-arrow recall of
  // previously sent user messages for the current case. The history array
  // is derived from the per-case `messages` state (user messages loaded on
  // mount via getChatHistory + appended on each send), so it is naturally
  // per-case -- switching cases reloads `messages` and this re-derives.
  // recallIndex is null when not recalling; otherwise it is an index into
  // inputHistory (oldest=0). draftRef holds the in-progress draft saved
  // when recall starts so ArrowDown past newest / Esc can restore it.
  const inputHistory = useMemo(
    () => messages.filter((m) => m.role === 'user').map((m) => m.content),
    [messages],
  );
  const recallIndex = useRef<number | null>(null);
  const draftRef = useRef('');

  // "Evaluate vs public data" progress state.
  const [evaluating, setEvaluating] = useState(false);
  const [evalPhase, setEvalPhase] = useState<string>('');

  // "Create custom check" modal (learned-rule creation, case locked).
  const [showRuleModal, setShowRuleModal] = useState(false);

  // Load persisted chat history for this case on mount (reconstructed from the
  // tamper-evident audit log server-side) so re-opening a case shows prior Q+A.
  useEffect(() => {
    let mounted = true;
    // reset recall state when switching cases
    // so stale recall index / draft from the prior case do not leak in.
    recallIndex.current = null;
    draftRef.current = '';
    setInput('');
    (async () => {
      try {
        const hist = await getChatHistory(caseId);
        if (mounted && hist.length) setMessages(hist);
        else if (mounted) setMessages([]);
      } catch {
        /* history load is best-effort; user can still send new messages */
      }
    })();
    return () => {
      mounted = false;
    };
  }, [caseId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // auto-resize the textarea to fit content up to
  // ~6 lines (max-h-32 = 8rem), then scroll internally. Re-runs on every
  // input change. Enter sends; Shift+Enter inserts a newline (handled in
  // onKeyDown below by only preventDefault-ing a bare Enter).
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const maxH = 128; // px, matches max-h-32 (8rem)
    ta.style.height = `${Math.min(ta.scrollHeight, maxH)}px`;
  }, [input]);

  async function send() {
    const question = input.trim();
    if (!question || loading) return;
    setInput('');
    // reset recall to "newest" after a send.
    // The sent message is appended to `messages` below, so inputHistory
    // re-derives with the new entry; recallIndex stays null (not recalling)
    // and any saved draft is cleared.
    recallIndex.current = null;
    draftRef.current = '';
    const userMsg: ChatMessage = { role: 'user', content: question, scope };
    setMessages((m) => [...m, userMsg]);
    setLoading(true);
    try {
      const resp = await chatWithCase(caseId, question, scope, messages, modelPath);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: resp.answer,
          path_used: resp.path_used,
          model_used: resp.model_used,
          run_id: resp.run_id,
          citations: resp.citations,
          scope,
        },
      ]);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || 'Chat request failed';
      addToast(detail, 'error');
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: `Error: ${detail}` },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
      return;
    }
    // readline-style up/down-arrow recall.
    // ArrowUp recalls the previous (older) sent message; ArrowDown moves
    // forward (newer); Esc (handled below) cancels recall + restores draft.
    // ArrowUp only triggers recall when the cursor is at the very start of
    // the input (selectionStart===0 && selectionEnd===0) so that pressing
    // Up inside a multiline message still moves the caret up a line (least
    // surprising; matches the pi webui harness reference). When recall
    // starts we save the in-progress draft so it can be restored.
    const ta = e.currentTarget;
    if (e.key === 'ArrowUp') {
      const atStart = ta.selectionStart === 0 && ta.selectionEnd === 0;
      if (!atStart || inputHistory.length === 0) return; // let caret move
      e.preventDefault();
      if (recallIndex.current === null) {
        draftRef.current = input; // save draft being typed
        recallIndex.current = inputHistory.length - 1;
      } else if (recallIndex.current > 0) {
        recallIndex.current -= 1;
      } else {
        return; // already at oldest; keep showing it
      }
      setInput(inputHistory[recallIndex.current]);
      // place caret at end of the recalled text (readline-style)
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      });
      return;
    }
    if (e.key === 'ArrowDown') {
      if (recallIndex.current === null) return; // not recalling; default caret
      e.preventDefault();
      if (recallIndex.current < inputHistory.length - 1) {
        recallIndex.current += 1;
        setInput(inputHistory[recallIndex.current]);
        requestAnimationFrame(() => {
          if (inputRef.current) {
            const len = inputRef.current.value.length;
            inputRef.current.setSelectionRange(len, len);
          }
        });
      } else {
        // past newest: cancel recall + restore the saved draft
        recallIndex.current = null;
        setInput(draftRef.current);
      }
      return;
    }
    if (e.key === 'Escape') {
      if (recallIndex.current === null) return; // not recalling; default
      e.preventDefault();
      recallIndex.current = null;
      setInput(draftRef.current);
      return;
    }
  }

  // "Evaluate vs public data" -- trigger a fresh public-data lookup,
  // poll until COMPLETED/FAILED, then run the LLM eval + post the discrepancy
  // list as an assistant message with citations.
  async function handleEvaluateVsPublic() {
    if (evaluating || loading) return;
    const queryValue = ticker || issuerName || '';
    if (!queryValue) {
      addToast('No issuer name or ticker to look up for this case', 'error');
      return;
    }
    const queryType = ticker ? 'ticker' : 'name';
    setEvaluating(true);
    setEvalPhase('Gathering public data...');
    try {
      const started = await triggerPublicLookup(caseId, queryType, queryValue);
      const runId = started.run_id;
      // Poll until COMPLETED or FAILED (cap at ~3 min).
      let status = started.status;
      const deadline = Date.now() + 180_000;
      while (status === 'RUNNING' && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const run = await getPublicDataRun(caseId, runId);
        status = run.status;
        if (run.results_count != null) {
          setEvalPhase(`Gathering public data... (${run.results_count} results)`);
        }
      }
      if (status !== 'COMPLETED') {
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              status === 'FAILED'
                ? 'Public data lookup failed. See the Public Data tab for details.'
                : 'Public data lookup timed out. Try again or check the Public Data tab.',
            scope: 'public',
          },
        ]);
        return;
      }
      setEvalPhase('Evaluating case claims vs public data...');
      const resp = await evaluateVsPublic(caseId, runId);
      const body = discrepanciesBlock(resp.discrepancies, resp.summary);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: body,
          path_used: resp.path_used,
          model_used: resp.model_used,
          run_id: resp.run_id,
          citations: resp.citations,
          scope: 'public',
        },
      ]);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || 'Evaluate-vs-public request failed';
      addToast(detail, 'error');
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: `Error: ${detail}`, scope: 'public' },
      ]);
    } finally {
      setEvaluating(false);
      setEvalPhase('');
    }
  }

  // "Create custom check" -- open the CreateRulePanel modal with the
  // case locked + the deficiency prefilled from the last user chat message.
  function handleCreateCustomCheck() {
    if (!user?.can_train_rules) return;
    setShowRuleModal(true);
  }

  function lastUserMessageText(): string {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].content;
    }
    return '';
  }

  // per-assistant-message action row.
  // Copy (clipboard + toast), Export .md (Blob download), Save (POST).
  async function handleCopy(idx: number) {
    const m = messages[idx];
    if (!m) return;
    try {
      await navigator.clipboard.writeText(m.content);
      addToast('Copied to clipboard', 'success');
    } catch {
      // Fallback for non-secure contexts / older browsers.
      const ta = document.createElement('textarea');
      ta.value = m.content;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        addToast('Copied to clipboard', 'success');
      } catch {
        addToast('Copy failed', 'error');
      }
      document.body.removeChild(ta);
    }
  }

  function handleExport(idx: number) {
    const m = messages[idx];
    if (!m) return;
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts =
      `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
      `${pad(now.getHours())}${pad(now.getMinutes())}`;
    const slug = (caseNumber || caseId).replace(/[^a-zA-Z0-9_-]/g, '_');
    const filename = `chat_${slug}_${ts}.md`;
    const header =
      `# Chat response\n\n` +
      `Case: ${caseNumber || caseId}\n` +
      (m.model_used ? `Model: ${m.model_used}\n` : '') +
      (m.path_used ? `Path: ${m.path_used}\n` : '') +
      `Saved: ${now.toISOString()}\n\n---\n\n`;
    const blob = new Blob([header + m.content + '\n'], {
      type: 'text/markdown;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleSave(idx: number) {
    const m = messages[idx];
    if (!m || savingIdx !== null) return;
    setSavingIdx(idx);
    try {
      await saveChatResponse(caseId, m.content, m.model_used, m.path_used);
      addToast('Response saved to case', 'success');
      onSavedResponsesChange?.();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail || 'Failed to save response';
      addToast(detail, 'error');
    } finally {
      setSavingIdx(null);
    }
  }

  const activeScopeNote = SCOPE_OPTIONS.find((o) => o.value === scope)?.note;
  const canTrain = !!user?.can_train_rules;
  const lockedCase: Case = {
    id: caseId,
    issuer_name: issuerName ?? null,
    ticker: ticker ?? null,
  } as unknown as Case;

  return (
    <div className="flex flex-col h-[calc(100vh-280px)] min-h-[420px] border border-slate-200 rounded-lg bg-white">
      {/* Scope selector +  action buttons */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-lg flex-wrap">
        <span className="text-xs font-medium text-slate-500 mr-1">Scope:</span>
        {SCOPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setScope(opt.value)}
            className={`text-xs px-2.5 py-1 rounded-full border font-medium transition ${
              scope === opt.value
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-[11px] text-slate-400 flex items-center gap-1">
          <Shield className="h-3 w-3" /> {activeScopeNote}
        </span>
        <span className="text-xs font-medium text-slate-500 ml-3 mr-1">Model:</span>
        {MODEL_PATH_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setModelPath(opt.value)}
            title={opt.note}
            className={`text-xs px-2.5 py-1 rounded-full border font-medium transition ${
              modelPath === opt.value
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-white text-slate-600 border-slate-300 hover:border-purple-400'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-[11px] text-slate-400">{MODEL_PATH_OPTIONS.find((o) => o.value === modelPath)?.note}</span>
        {/* Evaluate vs public data */}
        <button
          onClick={handleEvaluateVsPublic}
          disabled={evaluating || loading}
          title="Trigger a fresh public-data lookup + compare case claims against it"
          className="ml-auto text-xs px-2.5 py-1 rounded-full border font-medium bg-white text-slate-600 border-slate-300 hover:border-indigo-400 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {evaluating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Evaluate vs public data
        </button>
        {/* Create custom check (gated by can_train_rules) */}
        {canTrain && (
          <button
            onClick={handleCreateCustomCheck}
            disabled={evaluating || loading}
            title="Turn a deficiency from chat into a learned rule"
            className="text-xs px-2.5 py-1 rounded-full border font-medium bg-white text-slate-600 border-slate-300 hover:border-indigo-400 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Sparkles className="h-3 w-3" />
            Create custom check
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && !loading && !evaluating && (
          <div className="h-full flex flex-col items-center justify-center text-center text-slate-400">
            <MessageSquare className="h-10 w-10 mb-2" />
            <p className="text-sm">
              Ask a question about this case's documents or public data.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-3.5 py-2.5 text-sm ${
                m.role === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-800'
              }`}
            >
              {m.role === 'user' ? (
                <p className="whitespace-pre-wrap">{m.content}</p>
              ) : (
                <MarkdownRenderer content={m.content} />
              )}
              {m.role === 'assistant' && (
                <div className="mt-2 flex flex-col gap-1.5">
                  {m.citations && m.citations.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {m.citations.map((c, j) => (
                        <span
                          key={j}
                          title={c.snippet || c.ref}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600"
                        >
                          {citationIcon(c.type)}
                          {c.type === 'document' && c.filename
                            ? `${c.filename}${c.washed ? ' (washed)' : ''}`
                            : c.ref}
                        </span>
                      ))}
                    </div>
                  )}
                  {(m.path_used || m.model_used) && (
                    <div className="text-[11px] text-slate-400 flex items-center gap-2">
                      {m.path_used && (
                        <span className="inline-flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          path: {m.path_used}
                        </span>
                      )}
                      {m.model_used && <span>model: {m.model_used}</span>}
                    </div>
                  )}
                  {/* per-response actions */}
                  <div className="flex items-center gap-1.5 mt-1">
                    <button
                      onClick={() => handleCopy(i)}
                      title="Copy response to clipboard"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                    <button
                      onClick={() => handleExport(i)}
                      title="Download response as a .md file"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                    >
                      <Download className="h-3 w-3" /> Export .md
                    </button>
                    <button
                      onClick={() => handleSave(i)}
                      disabled={savingIdx === i}
                      title="Save this response to the case"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-50"
                    >
                      {savingIdx === i ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Bookmark className="h-3 w-3" />
                      )}
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 text-slate-500 rounded-lg px-3.5 py-2.5 text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Thinking...
            </div>
          </div>
        )}
        {evaluating && (
          <div className="flex justify-start">
            <div className="bg-slate-100 text-slate-500 rounded-lg px-3.5 py-2.5 text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {evalPhase || 'Working...'}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 px-3 py-3 rounded-b-lg">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="Ask a question..."
            className="flex-1 resize-none overflow-y-auto border border-slate-300 rounded-lg px-3 py-2 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-indigo-500 max-h-32"
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading || evaluating}
            className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white disabled:opacity-40 hover:bg-indigo-700"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Create custom check modal -- case locked + deficiency
          prefilled from the last user chat message. */}
      {showRuleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Sparkles className="text-primary-600" size={18} />
                <h3 className="text-sm font-semibold text-slate-800">
                  Create a custom check from this chat
                </h3>
              </div>
              <button
                onClick={() => setShowRuleModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <CreateRulePanel
                cases={[lockedCase]}
                casesLoading={false}
                lockedCaseId={caseId}
                initialDeficiency={lastUserMessageText()}
                onCreated={() => {
                  setShowRuleModal(false);
                  addToast(
                    'Learned rule created -- publish in Admin, Learned Rules',
                    'success'
                  );
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
